import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, FishSimple, List, X, Heart } from "@phosphor-icons/react";
import { useCart } from "@/context/CartContext";
import { useState } from "react";
import { useCustomer } from "@/context/CustomerContext";
import { UserCircle } from "@phosphor-icons/react";

function AccountLink() {
  const { customer } = useCustomer();
  return (
    <Link to={customer ? "/account" : "/account/login"} data-testid="nav-account" aria-label={customer ? "My account" : "Sign in"}
      title={customer ? `Signed in as ${customer.name}` : "Sign in / create account"}
      className="relative p-2 rounded-sm hover:bg-muted transition-colors flex items-center">
      <UserCircle size={22} weight={customer ? "fill" : "regular"} className={customer ? "text-[#2A4B3C]" : ""} />
    </Link>
  );
}

export default function Navbar() {
  const { totals, setOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { customer } = useCustomer();

  const linkCls = ({ isActive }) =>
    `text-sm uppercase tracking-widest whitespace-nowrap transition-colors hover:text-[#C84A31] ${
      isActive ? "text-[#C84A31]" : "text-foreground/70"
    }`;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAF9F6]/80 border-b border-border"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4 flex items-center justify-between gap-3 sm:gap-6">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2">
          <FishSimple size={28} weight="duotone" className="text-[#C84A31]" />
          <div className="leading-none">
            <div className="font-display text-xl tracking-tight">Reel Funny Goods</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground whitespace-nowrap hidden sm:block">Est. by the Jimenez family</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-5">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">Home</NavLink>
          <NavLink to="/shop" className={linkCls} data-testid="nav-shop">Shop</NavLink>
          <NavLink to="/quiz" className={linkCls} data-testid="nav-quiz">Quiz</NavLink>
          <NavLink to="/lounge" className={linkCls} data-testid="nav-lounge">Lounge</NavLink>
          <NavLink to="/reviews" className={linkCls} data-testid="nav-reviews">Reviews</NavLink>
          <NavLink to="/about" className={linkCls} data-testid="nav-about">About</NavLink>
          <NavLink to="/faq" className={linkCls} data-testid="nav-faq">FAQ</NavLink>
          <NavLink to="/donate" className={linkCls} data-testid="nav-donate"><span className="inline-flex items-center gap-1"><Heart size={13} weight="fill" className="text-[#C84A31]" />Support Us</span></NavLink>
          <NavLink
            to="/submit"
            data-testid="nav-submit"
            className={({ isActive }) =>
              `text-xs uppercase tracking-widest whitespace-nowrap font-display px-3 py-1.5 rounded-sm border transition-all hover:-translate-y-0.5 ${
                isActive
                  ? "bg-[#C84A31] text-white border-[#C84A31]"
                  : "bg-transparent text-[#C84A31] border-[#C84A31] hover:bg-[#C84A31] hover:text-white"
              }`
            }
          >
            Submit a joke
          </NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <AccountLink />
          <button
            onClick={() => setOpen(true)}
            data-testid="cart-toggle-btn"
            className="relative p-2 rounded-sm hover:bg-muted transition-colors"
            aria-label="Cart"
          >
            <ShoppingBag size={22} />
            {totals.count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-1 -right-1 bg-[#C84A31] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center"
              >
                {totals.count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen((v) => !v)}
            className="xl:hidden p-2 rounded-sm hover:bg-muted transition-colors"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="xl:hidden border-t border-border px-6 py-4 flex flex-col gap-4 bg-white">
          <NavLink to="/" end onClick={() => setMobileOpen(false)} className={linkCls}>Home</NavLink>
          <NavLink to="/shop" onClick={() => setMobileOpen(false)} className={linkCls}>Shop</NavLink>
          <NavLink to="/quiz" onClick={() => setMobileOpen(false)} className={linkCls}>Quiz</NavLink>
          <NavLink to="/lounge" onClick={() => setMobileOpen(false)} className={linkCls}>Lounge</NavLink>
          <NavLink to="/reviews" onClick={() => setMobileOpen(false)} className={linkCls} data-testid="mobile-nav-reviews">Reviews</NavLink>
          <NavLink to="/faq" onClick={() => setMobileOpen(false)} className={linkCls} data-testid="mobile-nav-faq">FAQ</NavLink>
          <NavLink to="/donate" onClick={() => setMobileOpen(false)} className={linkCls} data-testid="mobile-nav-donate">Support Us</NavLink>
          <NavLink to={customer ? "/account" : "/account/login"} onClick={() => setMobileOpen(false)} className={linkCls} data-testid="mobile-nav-account">{customer ? "My account" : "Sign in"}</NavLink>
          <NavLink to="/about" onClick={() => setMobileOpen(false)} className={linkCls}>About</NavLink>
          <NavLink
            to="/submit"
            onClick={() => setMobileOpen(false)}
            className="text-sm uppercase tracking-widest font-display text-[#C84A31] border border-[#C84A31] rounded-sm px-3 py-2 text-center"
          >
            Submit a joke
          </NavLink>
        </div>
      )}
    </header>
  );
}
